/**
 * Per-chart / per-occasion detail rows for compliance XLSX exports.
 * Includes HN, dates, and other stored chart fields when the category has them.
 */
import { getChecklist } from '../lib/checklists';
import { getCategoryById } from '../lib/complianceCategories';
import {
  filterSubmissionsForUi,
  resolveSubmissionDepartmentDisplayName,
} from '../lib/departmentUi';
import type { ReportingMonth } from '../lib/reportingMonth';
import type { ReportingSubmission } from '../services/dashboardData';
import {
  computeChartCountFromData,
  normalizeSubmissionShape,
} from '../services/submissionShape';
import { formatThaiMonthYYBuddhist } from './dateThai';

/** Fields exported as one cell per chart index (parallel arrays on the submission). */
export const CATEGORY_DETAIL_FIELD_WHITELIST: Record<string, readonly string[]> = {
  needleandsharp: ['dates', 'shifts', 'answers'],
  med_error: ['dates', 'answers'],
  siren_bundle: ['dates', 'hn', 'answers'],
  nurse_notes: [
    'dates',
    'answers',
    'disease',
    'focus',
    'treatments',
    'problems',
    'suggestions',
    'supervisionRecords',
  ],
};

const FIELD_LABELS: Record<string, string> = {
  dates: 'วันที่',
  hn: 'HN',
  shifts: 'เวร',
  disease: 'โรค',
  focus: 'จำนวน Focus list ที่สอดคล้องกับการรักษาของแพทย์',
  treatments: 'จำนวนการรักษาที่แพทย์บันทึก',
  problems: 'ปัญหาที่พบภาพรวม',
  suggestions: 'ข้อเสนอแนะอื่นๆ',
  supervisionRecords: 'รายการนิเทศ',
};

const SHIFT_LABELS: Record<string, string> = {
  morning: 'เช้า',
  evening: 'บ่าย',
  night: 'ดึก',
};

const CHART_INDEX_LABEL: Record<string, string> = {
  siren_bundle: 'ลำดับ HN',
  needleandsharp: 'ครั้งที่',
  med_error: 'สัปดาห์',
  nurse_notes: 'แผนภูมิ',
};

type SupervisionRecord = {
  recipients?: string[];
  recipientsDetail?: string;
  activities?: string[];
  issues?: string[];
  issueOther?: string;
  problemDetail?: string;
  solution?: string;
  followUp?: string;
};

function canonicalCategoryKey(categoryId: string): string {
  return getCategoryById(categoryId)?.id ?? categoryId;
}

function detailFieldsForCategory(categoryId: string): readonly string[] {
  const key = canonicalCategoryKey(categoryId);
  return CATEGORY_DETAIL_FIELD_WHITELIST[key] ?? CATEGORY_DETAIL_FIELD_WHITELIST[categoryId] ?? [];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function cellAt(source: unknown, index: number): unknown {
  if (!Array.isArray(source)) return undefined;
  return source[index];
}

function formatPrimitive(value: unknown, field?: string): string {
  if (value == null) return '—';
  if (field === 'shifts' && typeof value === 'string') {
    return SHIFT_LABELS[value] ?? value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => formatPrimitive(item, field))
      .filter((item) => item !== '—')
      .join(', ');
    return joined || '—';
  }
  return String(value);
}

function coerceDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.valueOf()) ? null : value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.valueOf()) ? null : parsed;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = value as { toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      try {
        const date = maybe.toDate();
        return date instanceof Date && !Number.isNaN(date.valueOf()) ? date : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

function formatDateValue(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatDateValue(first);
  }
  const date = coerceDate(value);
  if (date) return date.toLocaleDateString('th-TH', { dateStyle: 'medium' });
  return formatPrimitive(value, 'dates');
}

function formatHnValue(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatHnValue(first);
  }
  return formatPrimitive(value, 'hn');
}

function formatAnswerCell(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatAnswerCell(first);
  }
  return String(value);
}

function formatSupervisionRecord(value: unknown): string {
  const record = asRecord(value) as SupervisionRecord;
  const parts = [
    record.recipients?.length ? `ผู้รับ: ${record.recipients.join(', ')}` : '',
    record.recipientsDetail?.trim() ? `ระบุ: ${record.recipientsDetail.trim()}` : '',
    record.activities?.length ? `กิจกรรม: ${record.activities.join(', ')}` : '',
    record.issues?.length ? `ประเด็น: ${record.issues.join(', ')}` : '',
    record.issueOther?.trim() ? `อื่นๆ: ${record.issueOther.trim()}` : '',
    record.problemDetail?.trim() ? `ปัญหา: ${record.problemDetail.trim()}` : '',
    record.solution?.trim() ? `แก้ไข: ${record.solution.trim()}` : '',
    record.followUp?.trim() ? `ติดตาม: ${record.followUp.trim()}` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' | ') : '—';
}

function checklistAnswerLabels(categoryId: string): string[] {
  const category = getCategoryById(categoryId);
  const sections = getChecklist(category?.title ?? categoryId);
  if (!sections.length) return [];
  const includeSectionTitle = sections.length > 1;
  return sections.flatMap((section) =>
    section.items.map((item) =>
      section.title && includeSectionTitle ? `${section.title}: ${item}` : item,
    ),
  );
}

function answerColumnCount(categoryId: string, submissions: ReportingSubmission[]): number {
  const fromChecklist = checklistAnswerLabels(categoryId).length;
  let maxFromData = 0;
  submissions.forEach((submission) => {
    const shape = normalizeSubmissionShape(submission as Record<string, unknown>);
    shape.answers.forEach((row) => {
      if (Array.isArray(row)) maxFromData = Math.max(maxFromData, row.length);
    });
  });
  return Math.max(fromChecklist, maxFromData, 0);
}

function chartIndexLabel(categoryId: string): string {
  const key = canonicalCategoryKey(categoryId);
  return CHART_INDEX_LABEL[key] ?? CHART_INDEX_LABEL[categoryId] ?? 'แผนภูมิ';
}

function chartHasContent(
  fields: readonly string[],
  raw: Record<string, unknown>,
  shapeAnswers: string[][],
  chartIndex: number,
): boolean {
  return fields.some((field) => {
    if (field === 'answers') {
      const row = shapeAnswers[chartIndex];
      return Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '');
    }
    const value = cellAt(raw[field], chartIndex);
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.some((item) => String(item ?? '').trim() !== '');
    if (typeof value === 'object') return Object.keys(asRecord(value)).length > 0;
    return String(value).trim() !== '';
  });
}

export type SubmissionDetailExportParams = {
  categoryId: string;
  submissions: ReportingSubmission[];
  deptIdToName: Map<string, string>;
};

/**
 * One row per chart/occasion with category detail fields (HN, date, answers, …).
 * Categories without a whitelist return empty headers/rows.
 */
export function buildSubmissionDetailExportData(
  params: SubmissionDetailExportParams,
): { headers: string[]; rows: string[][] } {
  const fields = detailFieldsForCategory(params.categoryId);
  if (!fields.length) {
    return { headers: [], rows: [] };
  }

  const submissions = filterSubmissionsForUi(params.submissions);
  const answerCount = fields.includes('answers')
    ? answerColumnCount(params.categoryId, submissions)
    : 0;
  const answerHeaders =
    answerCount > 0
      ? Array.from({ length: answerCount }, (_, index) => `ข้อ${index + 1}`)
      : [];

  const scalarFields = fields.filter((field) => field !== 'answers');
  const headers = [
    'หน่วยงาน',
    'เดือนรายงาน',
    chartIndexLabel(params.categoryId),
    'ผู้ส่ง',
    ...scalarFields.map((field) => FIELD_LABELS[field] ?? field),
    ...answerHeaders,
  ];

  const rows: string[][] = [];

  submissions.forEach((submission) => {
    const raw = submission as Record<string, unknown>;
    const shape = normalizeSubmissionShape(raw);
    const chartCount = Math.max(
      1,
      computeChartCountFromData(raw, shape.answers.length || 1),
      ...fields.map((field) => (Array.isArray(raw[field]) ? (raw[field] as unknown[]).length : 0)),
    );

    for (let chartIndex = 0; chartIndex < chartCount; chartIndex += 1) {
      if (!chartHasContent(fields, raw, shape.answers, chartIndex)) continue;

      const scalarCells = scalarFields.map((field) => {
        const value = cellAt(raw[field], chartIndex);
        if (field === 'dates') return formatDateValue(value);
        if (field === 'hn') return formatHnValue(value);
        if (field === 'supervisionRecords') return formatSupervisionRecord(value);
        return formatPrimitive(value, field);
      });

      const answerRow = shape.answers[chartIndex] ?? [];
      const answerCells =
        answerCount > 0
          ? Array.from({ length: answerCount }, (_, answerIndex) =>
              formatAnswerCell(answerRow[answerIndex]),
            )
          : [];

      const reportingMonth =
        typeof submission.reportingMonth === 'string' && submission.reportingMonth
          ? formatThaiMonthYYBuddhist(submission.reportingMonth as ReportingMonth)
          : '—';

      rows.push([
        resolveSubmissionDepartmentDisplayName(submission, params.deptIdToName),
        reportingMonth,
        String(chartIndex + 1),
        submission.submittedByName?.trim() ||
          submission.userDisplayName?.trim() ||
          '—',
        ...scalarCells,
        ...answerCells,
      ]);
    }
  });

  return { headers, rows };
}
